import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What happens during the five days of SAINT® TMS?",
    answer:
      "Each day includes ten sessions of about 10 minutes each, with 50-minute breaks between them. You stay awake, no anesthesia is used. Between sessions, most patients read, work, or rest. The treatment is outpatient — you return to your hotel or home each evening. The five days are consecutive, Monday through Friday.",
  },
  {
    question: "Does SAINT® hurt?",
    answer:
      "Most people describe a tapping or knocking sensation on the scalp during pulses. Headache was the most common side effect in the sham-controlled trial. Scalp discomfort and facial twitching are also common and usually lessen over the first day or two. Over-the-counter pain relief is often enough. Tell your team about any discomfort.",
  },
  {
    question: "What do patients do between sessions?",
    answer:
      "Between sessions, most patients read, work on a laptop, rest, or listen to music. The 50-minute breaks are built into the protocol. You stay at the clinic for most of each day. Some patients bring work or entertainment; others simply rest.",
  },
  {
    question: "Do I need an MRI before SAINT®?",
    answer:
      "Yes. SAINT® uses a structural and functional MRI to select an individualized stimulation target. The MRI must be completed and processed before the first treatment day. Standard MRI safety screening applies, and people who cannot safely have an MRI are not candidates.",
  },
  {
    question: "Can I drive after SAINT® sessions?",
    answer:
      "Yes, in most cases. SAINT® is delivered while you are awake and does not use anesthesia or sedation. Patients are generally permitted to drive afterward. If you experience a side effect that affects your ability to drive, such as a significant headache, tell the treatment team.",
  },
  {
    question: "What happens after the five days?",
    answer:
      "After treatment, a follow-up plan is arranged. This may include medication, psychotherapy, and the possibility of another course if symptoms return. In the trials, follow-up was four weeks. Discuss a maintenance plan with your psychiatrist before starting treatment.",
  },
];

export const pageWhatToExpect: LearnPageData = {
  slug: "what-to-expect-during-saint-tms",
  title: "What Happens During the 5 Days of SAINT®? | Neuro Wellness",
  description:
    "What happens during the five days of SAINT® TMS: before treatment, Day 1, Days 2-4, Day 5, after treatment, targeting, sessions, breaks, sensations, outpatient care, and follow-up.",
  canonical: "/learn/what-to-expect-during-saint-tms",
  h1: "What Happens During the 5 Days of SAINT® TMS?",
  breadcrumbLabel: "What to Expect During SAINT®",
  crumbs: baseCrumbs("What to Expect During SAINT®"),
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is delivered over five consecutive days, with ten sessions per day of about 10 minutes each and 50-minute breaks between them. Patients stay awake — no anesthesia is used — and the treatment is outpatient, meaning you return to your hotel or home each evening. Before treatment, you complete a psychiatric evaluation and an fMRI scan to determine your individualized stimulation target. This page is a patient guide covering what happens before treatment, on each day, and after treatment, including targeting, sessions, breaks, common sensations, and follow-up.",
  keyTakeaways: [
    "Five consecutive days, ten sessions per day.",
    "Each session is about 10 minutes with 50-minute breaks.",
    "Patients stay awake — no anesthesia.",
    "Outpatient — return home each evening.",
    "An fMRI is completed before treatment to set the target.",
  ],

  sections: [
    {
      heading: "Before Treatment",
      body: (
        <>
          {P(
            "Before the treatment week, you complete a psychiatric evaluation to confirm eligibility and an fMRI scan. The fMRI — both structural and functional — is used to compute your individualized stimulation target in the left dorsolateral prefrontal cortex. Standard MRI safety screening applies. The MRI must be completed and processed before the first treatment day.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/fmri-guided-tms-for-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                fMRI-guided TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Day 1",
      body: (
        <>
          {P(
            "On Day 1, you arrive at the clinic and are positioned for treatment. The coil is placed at your individualized target, determined from your fMRI. The first session delivers intermittent theta burst stimulation — you feel a tapping or knocking sensation on the scalp. After about 10 minutes, the session ends and a 50-minute break begins. This repeats ten times. Most patients spend the full day at the clinic.",
          )}
        </>
      ),
    },
    {
      heading: "Days 2–4",
      body: (
        <>
          {P(
            "Days 2 through 4 follow the same pattern: ten sessions per day with 50-minute breaks. Between sessions, most patients read, work on a laptop, rest, or listen to music. Scalp discomfort and headache, if present, usually lessen over the first day or two. The treatment team monitors you throughout and can adjust coil position or intensity if needed.",
          )}
        </>
      ),
    },
    {
      heading: "Day 5",
      body: (
        <>
          {P(
            "Day 5 is the final treatment day — the same ten-session schedule. After the last session, the treatment team reviews how the week went and discusses next steps. You may be asked to complete symptom rating scales so your response can be tracked. A follow-up plan is arranged before you leave.",
          )}
        </>
      ),
    },
    {
      heading: "After Treatment",
      body: (
        <>
          {P(
            "After the five days, a follow-up plan is arranged. This may include medication, psychotherapy, and the possibility of another course if symptoms return. In the clinical trials, follow-up was four weeks. Discuss a maintenance plan with your psychiatrist before starting treatment. If you traveled from out of town, follow-up may be coordinated with your home providers.",
          )}
        </>
      ),
    },
    {
      heading: "Targeting",
      body: (
        <>
          {P(
            "SAINT® uses fMRI to compute your individualized stimulation target — not scalp measurements. This is a key difference from conventional TMS, which targets the left dorsolateral prefrontal cortex using scalp landmarks. The fMRI-based target is computed from how your prefrontal cortex connects to deeper mood circuits.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/fmri-guided-tms-for-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                fMRI-guided TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Treatment Sessions and Breaks",
      body: (
        <>
          {P(
            "Each session delivers intermittent theta burst stimulation — bursts of three pulses at 50 Hz — for about 10 minutes, with 1,800 pulses per session. After each session, a 50-minute break allows the brain to respond. Ten sessions per day for five days totals 50 sessions. You stay awake throughout; no anesthesia or sedation is used.",
          )}
        </>
      ),
    },
    {
      heading: "Common Sensations",
      body: (
        <>
          {P(
            "Most people describe a tapping or knocking sensation on the scalp during pulses. Headache was the most common side effect in the sham-controlled trial. Scalp discomfort and facial twitching are also common and usually lessen over the first day or two. Over-the-counter pain relief is often enough. Tell your team about any discomfort so coil position or intensity can be adjusted.",
          )}
        </>
      ),
    },
    {
      heading: "Outpatient Nature and Transportation",
      body: (
        <>
          {P(
            "SAINT® is outpatient — no overnight hospital stay. You return to your hotel or home each evening. Because no anesthesia is used, you can generally drive yourself. If you experience a side effect that affects your ability to drive, tell the treatment team. If you traveled from out of town, plan to stay locally for the week.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/traveling-for-treatment"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                traveling for treatment
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Questions to Ask the Treatment Team",
      body: (
        <>
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              How will my target be determined?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What should I bring for the breaks between sessions?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What side effects should I expect, and how are they managed?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              How will my response be measured?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What is the follow-up plan after the five days?
            </li>
          </ul>
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/what-to-expect-during-saint-tms#webpage`,
        name: "What Happens During the 5 Days of SAINT® TMS?",
        url: `${SITE_URL}/learn/what-to-expect-during-saint-tms`,
        description:
          "What happens during the five days of SAINT® TMS: before, Day 1, Days 2-4, Day 5, after, targeting, sessions, breaks, sensations, and follow-up.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/learn/what-to-expect-during-saint-tms#faq`),
      breadcrumbJsonld(baseCrumbs("What to Expect During SAINT®")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "How Quickly Can SAINT® TMS Work?",
      href: "/learn/how-fast-does-saint-tms-work",
      description: "Treatment duration vs when improvement is noticed",
    },
    {
      title: "fMRI-Guided TMS for Depression",
      href: "/learn/fmri-guided-tms-for-depression",
      description: "How personalized brain targeting works",
    },
  ],
};
